import * as React from 'react';

import {CardTwoLine} from 'shells/card/card-two-line';
import {colors} from 'shells/colors/colors';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {StatusBadge} from 'shells/status-badge/status-badge';

import type {
    MeetingValidityIssueFragment as MeetingValidityIssue,
    MeetingFragment,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useGetCalendarSync} from '../../setup/calendar/graphql/hooks/use-get-calendar-sync';

import {CalendarModalComponent} from '../calendar-modal-component/calendar-modal-component';
import {ConferencingModalWrapper} from '../location/conferencing-modals/conferencing-modal-wrapper';
import type {ConferencingModalType} from '../location/conferencing-modals/conferencing-modal-wrapper';

import {MEETING_VALIDITY_ISSUES} from './consts';
import {CurrentTimeInZone} from './current-time-in-zone';
import {
    getMultipleIssuesText,
    getCustomizedIssueDescription,
    getMultipleCustomizedIssueDescriptions,
} from './helpers';

type Props = {
    hostId: string;
    meeting: MeetingFragment;
    name: string;
    firstName: string | null | undefined;
    timezone: string;
    issues?: MeetingValidityIssue[];
    refetchMeeting: () => any;
};

export function MeetingHostCard(props: Props) {
    const [isSyncModalOpen, setIsSyncModalOpen] = React.useState<boolean>(false);
    const [conferencingModalType, setConferencingModalType] = React.useState<
        ConferencingModalType | null | undefined
    >(null);

    const {user} = useGetSessionUser();
    const {
        calendarSync,
        isLoading,
        refetch: refetchCalendarSync,
        errorMessage,
    } = useGetCalendarSync();

    const onClose = () => {
        setConferencingModalType(null);
        props.refetchMeeting();
    };

    return (
        <>
            <CardTwoLine
                accentColor={props.issues ? colors.red : colors.green}
                topRow={
                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                        <div>{props.name}</div>
                        {props.issues && (
                            <StatusBadge
                                size='small'
                                level='alert--text'
                                includeIcon={true}
                                text={
                                    props.issues.length > 1
                                        ? getMultipleIssuesText(props.issues)
                                        : // @ts-expect-error
                                          MEETING_VALIDITY_ISSUES[props.issues[0].issueType].text
                                }
                                detail={
                                    props.issues.length > 1
                                        ? getMultipleCustomizedIssueDescriptions(
                                              props.issues,
                                              props.firstName || props.name,
                                              Boolean(user && user.id === props.hostId),
                                              () => setIsSyncModalOpen(true),
                                              (type: ConferencingModalType) =>
                                                  setConferencingModalType(type)
                                          )
                                        : getCustomizedIssueDescription(
                                              // @ts-expect-error
                                              props.issues[0],
                                              props.firstName || props.name,
                                              Boolean(user && user.id === props.hostId),
                                              () => setIsSyncModalOpen(true),
                                              (type: ConferencingModalType) =>
                                                  setConferencingModalType(type)
                                          )
                                }
                                detailMaxWidth={250}
                            />
                        )}
                    </StackLegacy>
                }
                bottomRow={
                    <CurrentTimeInZone
                        timezone={props.timezone}
                        currentUserTimezone={user && user.timezone}
                    />
                }
                isLargeWithSmallBottomRow={true}
            />
            {isSyncModalOpen && (
                <CalendarModalComponent
                    errorMessage={errorMessage}
                    calendarSync={calendarSync}
                    isLoading={isLoading}
                    refetch={refetchCalendarSync}
                    onSuccess={props.refetchMeeting}
                    onClose={() => setIsSyncModalOpen(false)}
                />
            )}
            {conferencingModalType && (
                <ConferencingModalWrapper
                    modalType={conferencingModalType}
                    meeting={props.meeting}
                    closeModal={onClose}
                />
            )}
        </>
    );
}
